import { fromEntries } from "./browserCompat";
import {
  SEGMENT_ORDER,
  SEGMENT_SLOTS,
  SEGMENT_TYPES,
  getSegmentDefinition,
} from "./segmentRegistry";
import { updateSegmentVisualStyle } from "./segmentVisualStyle";
import {
  buildWhatsAppUrl,
  fullBusinessAddress,
  resolveLandingMedia,
} from "./landingMedia";

const STYLE_ALIASES = {
  "1": "style-1",
  classic: "style-1",
  clasica: "style-1",
  default: "style-1",
  "catalog-basic": "style-1",
  "style-1": "style-1",
  "2": "style-2",
  commercial: "style-2",
  comercial: "style-2",
  split: "style-2",
  "style-2": "style-2",
  "3": "style-3",
  minimal: "style-3",
  minimalista: "style-3",
  dark: "style-3",
  "style-3": "style-3",
};

export const DEFAULT_LANDING_SEGMENT_ORDER = Object.freeze([
  SEGMENT_TYPES.NAVBAR,
  SEGMENT_TYPES.HERO,
  SEGMENT_TYPES.SPOTLIGHT,
  SEGMENT_TYPES.FEATURED,
  SEGMENT_TYPES.BRANDS,
  SEGMENT_TYPES.CATEGORIES,
  SEGMENT_TYPES.ABOUT,
  SEGMENT_TYPES.HIGHLIGHTS,
  SEGMENT_TYPES.CONTACT,
  SEGMENT_TYPES.FOOTER,
]);
const STRUCTURAL_TYPES = new Set(["navbar", "footer"]);
const MAX_SEGMENTS = 40;
const MAX_HOME_SEGMENTS = MAX_SEGMENTS - STRUCTURAL_TYPES.size;

function clone(value) {
  if (value === undefined) return undefined;
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function deepMerge(base, source) {
  if (source === undefined) return clone(base);
  if (!source || typeof source !== "object" || Array.isArray(source)) return clone(source);
  const output = base && typeof base === "object" && !Array.isArray(base) ? clone(base) : {};
  Object.entries(source).forEach(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      output[key] = deepMerge(output[key], value);
    } else {
      output[key] = clone(value);
    }
  });
  return output;
}

const VISIBILITY_PATHS = Object.freeze({
  hero: "sections.showHero",
  spotlight: "sections.showSpotlight",
  featured: "sections.showFeatured",
  brands: "sections.showBrands",
  categories: "sections.showCategories",
  about: "sections.showAbout",
  highlights: "sections.showHighlights",
  imageCarousel: "sections.showImageCarousel",
  testimonials: "sections.showTestimonials",
  contact: "sections.showContact",
});

function setAtPath(target, path, value) {
  const parts = String(path || "").split(".").filter(Boolean);
  let cursor = target;
  parts.forEach((key, index) => {
    if (index === parts.length - 1) {
      cursor[key] = value;
      return;
    }
    if (!cursor[key] || typeof cursor[key] !== "object" || Array.isArray(cursor[key])) cursor[key] = {};
    cursor = cursor[key];
  });
  return target;
}

function cleanId(value, fallback) {
  const cleaned = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[^a-z0-9]+/, "")
    .replace(/-+$/g, "")
    .slice(0, 80);
  return cleaned || fallback;
}

function canonicalSegmentId(type) {
  const value = String(type || "").trim();
  return value ? cleanId(value, value) : "";
}

function isCanonicalSegment(instance) {
  const type = String(instance?.type || "").trim();
  if (!type || type === "custom") return false;
  return cleanId(instance?.id, "") === canonicalSegmentId(type);
}

function asSegmentCollection(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  for (const key of ["segments", "order", "segmentOrder"]) {
    if (Array.isArray(value[key])) return value[key];
  }
  return [];
}

function segmentTypeFrom(value) {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return "";
  return String(value.type || value.segmentType || value.id || "").trim();
}

function uniqueRenderableTypes(value) {
  const seen = new Set();
  const types = [];
  for (const item of asSegmentCollection(value)) {
    const type = segmentTypeFrom(item);
    if (!type || seen.has(type) || !getSegmentDefinition(type)) continue;
    seen.add(type);
    types.push(type);
  }
  return types;
}

function completeSegmentDefaults(order, fallback) {
  const completed = [...order];
  const seen = new Set(completed);
  for (const type of [
    ...uniqueRenderableTypes(fallback),
    ...DEFAULT_LANDING_SEGMENT_ORDER,
  ]) {
    if (seen.has(type)) continue;
    seen.add(type);
    completed.push(type);
  }
  return completed;
}

function keepValidSlotOrder(order) {
  const header = [];
  const home = [];
  const footer = [];
  for (const type of order) {
    const definition = getSegmentDefinition(type);
    if (!definition) continue;
    if (definition.slot === SEGMENT_SLOTS.SHELL_HEADER) header.push(type);
    else if (definition.slot === SEGMENT_SLOTS.SHELL_FOOTER) footer.push(type);
    else home.push(type);
  }
  return [...header, ...home, ...footer];
}

export function normalizeLandingSegmentOrder(
  value,
  fallback = DEFAULT_LANDING_SEGMENT_ORDER
) {
  const requested = uniqueRenderableTypes(value);
  return keepValidSlotOrder(completeSegmentDefaults(requested, fallback));
}

export const normalizeLandingLayout = normalizeLandingSegmentOrder;

export function resolveLandingLayout(
  value,
  fallback = DEFAULT_LANDING_SEGMENT_ORDER
) {
  return normalizeLandingSegmentOrder(value, fallback)
    .map((type) => getSegmentDefinition(type))
    .filter(Boolean);
}

export function defaultLandingSegmentInstances() {
  return DEFAULT_LANDING_SEGMENT_ORDER.map((type) => ({ id: type, type }));
}

export function normalizeLandingSegmentInstances(value) {
  const source = asSegmentCollection(value);
  if (!source.length && !Array.isArray(value) && !Array.isArray(value?.segments)) {
    return defaultLandingSegmentInstances();
  }

  const structuralSource = fromEntries(
    source
      .map((item) => (typeof item === "object" && item ? item : { id: item, type: item }))
      .filter((item) => STRUCTURAL_TYPES.has(segmentTypeFrom(item)))
      .map((item) => [segmentTypeFrom(item), item])
  );
  const usedIds = new Set(SEGMENT_ORDER.map(canonicalSegmentId));
  const canonicalSeen = new Set();
  const home = [];

  source.slice(0, MAX_SEGMENTS).forEach((item) => {
    if (home.length >= MAX_HOME_SEGMENTS) return;
    const type = segmentTypeFrom(item);
    const definition = getSegmentDefinition(type);
    if (!definition || STRUCTURAL_TYPES.has(type)) return;

    const custom = type === "custom";
    const requestedId = typeof item === "object" && item
      ? item.id || (custom ? `seccion-${home.length + 1}` : type)
      : (custom ? `seccion-${home.length + 1}` : type);
    const canonicalId = custom ? "" : canonicalSegmentId(type);
    const base = cleanId(requestedId, custom ? `seccion-${home.length + 1}` : canonicalId);
    const canonicalRequest = !custom && base === canonicalId;
    let id;
    if (canonicalRequest && !canonicalSeen.has(type)) {
      canonicalSeen.add(type);
      id = canonicalId;
    } else {
      id = base;
      let suffix = 2;
      while (usedIds.has(id)) {
        id = `${base.slice(0, 72)}-${suffix}`;
        suffix += 1;
      }
      usedIds.add(id);
    }

    const segment = { id, type };
    if (typeof item === "object" && item) {
      if (item.anchor) segment.anchor = cleanId(item.anchor, id);
      if (item.visible === false) segment.visible = false;
      if (item.data && typeof item.data === "object") segment.data = clone(item.data);
      if (item.visualStyle && typeof item.visualStyle === "object") {
        segment.visualStyle = clone(item.visualStyle);
      }
      if (item.style && typeof item.style === "object") {
        segment.style = clone(item.style);
      }
    }
    home.push(segment);
  });

  const structuralSegment = (type) => {
    const raw = structuralSource[type];
    return {
      id: type,
      type,
      ...(raw?.visible === false ? { visible: false } : {}),
      ...(raw?.visualStyle && typeof raw.visualStyle === "object"
        ? { visualStyle: clone(raw.visualStyle) }
        : {}),
    };
  };

  return [
    structuralSegment("navbar"),
    ...home,
    structuralSegment("footer"),
  ];
}

export function getHomeLandingSegmentInstances(value, sections = null) {
  const home = normalizeLandingSegmentInstances(value).filter((segment) => {
    const definition = getSegmentDefinition(segment.type);
    return definition?.slot === SEGMENT_SLOTS.HOME;
  });
  if (home.length || !sections || typeof sections !== "object") return home;

  // Defensa para respuestas legacy durante despliegues escalonados: si el layout
  // no trae ningún bloque de home pero las flags históricas siguen activas,
  // reconstruimos sólo esas instancias. El editor moderno apaga las flags al
  // eliminar una sección, por lo que una landing vacía intencional sigue vacía.
  return defaultLandingSegmentInstances().filter((segment) => {
    const definition = getSegmentDefinition(segment.type);
    if (definition?.slot !== SEGMENT_SLOTS.HOME) return false;
    const visibilityPath = VISIBILITY_PATHS[segment.type];
    const visibilityKey = String(visibilityPath || "").split(".").pop();
    return Boolean(visibilityKey) && sections[visibilityKey] !== false;
  });
}

function getAtPath(source, path) {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, key) => current?.[key], source);
}

export function landingSegmentHasContent(type, landing = {}, productsEnabled = true) {
  const business = landing?.business || {};

  if (type === "hero") {
    const hero = landing?.hero || {};
    const hasMedia = Boolean(
      resolveLandingMedia(hero.imageUrl) ||
        (Array.isArray(hero.slides) &&
          hero.slides.some((slide) => resolveLandingMedia(slide?.url)))
    );
    const hasCopy = Boolean(
      hero.eyebrow ||
        hero.title ||
        hero.subtitle ||
        business.name ||
        (Array.isArray(hero.rotatingPhrases) && hero.rotatingPhrases.some(Boolean))
    );
    const primary =
      Boolean(hero.primaryCtaLabel && hero.primaryCtaUrl) &&
      (!hero.primaryCtaRequiresProducts || productsEnabled);
    const secondary =
      Boolean(hero.secondaryCtaLabel && hero.secondaryCtaUrl) &&
      (!hero.secondaryCtaRequiresProducts || productsEnabled);
    return hasMedia || hasCopy || primary || secondary;
  }

  if (type === "spotlight") {
    const spotlight = landing?.catalog?.spotlight || {};
    if (spotlight.enabled !== true) return false;
    const hasMedia = Boolean(resolveLandingMedia(spotlight.logoUrl));
    const hasCopy = Boolean(spotlight.eyebrow || spotlight.title || spotlight.description);
    const hasCta =
      Boolean(spotlight.ctaLabel && spotlight.ctaUrl) &&
      (!spotlight.ctaRequiresProducts || productsEnabled);
    return hasMedia || hasCopy || hasCta;
  }

  if (type === "about") {
    const about = landing?.about || {};
    const hasCta =
      Boolean(about.ctaLabel && about.ctaUrl) &&
      (!about.ctaRequiresProducts || productsEnabled);
    return Boolean(
      resolveLandingMedia(about.imageUrl) ||
        about.eyebrow ||
        about.title ||
        about.description ||
        hasCta
    );
  }

  if (type === "highlights") {
    const intro = landing?.highlightsIntro || {};
    const items = Array.isArray(landing?.highlights) ? landing.highlights : [];
    return Boolean(
      intro.eyebrow ||
        intro.title ||
        intro.subtitle ||
        items.some((item) => item?.title || item?.description || resolveLandingMedia(item?.imageUrl) || item?.youtubeUrl)
    );
  }

  if (type === "imageCarousel") {
    const carousel = landing?.imageCarousel || {};
    const items = Array.isArray(carousel.items) ? carousel.items : [];
    return Boolean(
      carousel.eyebrow ||
        carousel.title ||
        carousel.subtitle ||
        items.some((item) => resolveLandingMedia(item?.imageUrl) || item?.caption)
    );
  }

  if (type === "testimonials") {
    const testimonials = landing?.testimonials || {};
    const items = Array.isArray(testimonials.items) ? testimonials.items : [];
    return Boolean(
      testimonials.eyebrow ||
        testimonials.title ||
        testimonials.subtitle ||
        items.some((item) => item?.quote || item?.name || item?.role || resolveLandingMedia(item?.avatarUrl))
    );
  }

  if (type === "custom") {
    const custom = landing?.custom || {};
    return Boolean(
      custom.eyebrow ||
      custom.title ||
      custom.description ||
      resolveLandingMedia(custom.imageUrl) ||
      custom.youtubeUrl ||
      custom.calendlyUrl ||
      (custom.ctaLabel && custom.ctaUrl)
    );
  }

  if (type === "contact") {
    const contact = landing?.contact || {};
    const whatsappUrl = buildWhatsAppUrl(business.whatsapp, contact.whatsappMessage);
    const hasDetails = Boolean(
      fullBusinessAddress(business) || business.schedule || business.phone || business.email
    );
    const hasWhatsapp = Boolean(whatsappUrl && contact.whatsappCtaLabel);
    const hasDetailsCta = Boolean(contact.detailsCtaLabel && contact.detailsCtaUrl);
    return Boolean(
      contact.title ||
        contact.subtitle ||
        hasDetails ||
        hasWhatsapp ||
        hasDetailsCta ||
        resolveLandingMedia(contact.backgroundImageUrl)
    );
  }

  return true;
}

export function isLandingSegmentInstanceVisible(
  instance,
  landing = {},
  productsEnabled = true
) {
  const definition = getSegmentDefinition(instance?.type);
  if (!definition || definition.slot !== SEGMENT_SLOTS.HOME) return false;
  if (definition.requiresProducts && !productsEnabled) return false;

  if (isCanonicalSegment(instance)) {
    if (definition.visibilityPath && getAtPath(landing, definition.visibilityPath) === false) {
      return false;
    }
    if (
      (definition.parentVisibilityPaths || []).some(
        (path) => getAtPath(landing, path) === false
      )
    ) {
      return false;
    }
  } else if (instance.visible === false) {
    return false;
  }

  // Una sección libre con únicamente imagen de fondo también es contenido visual.
  // Antes se descartaba ANTES de ejecutar LandingCustomSection, aunque tuviera
  // style.background.imageUrl válido; sus fondos jamás podían aparecer.
  if (instance.type === "custom" && resolveLandingMedia(
    instance.style?.background?.imageUrl || instance.visualStyle?.backgroundImageUrl
  )) return true;

  return landingSegmentHasContent(instance.type, landing, productsEnabled);
}

export function hasVisibleLandingSegmentType(landing = {}, type, productsEnabled = true) {
  return getHomeLandingSegmentInstances(landing?.layout).some((instance) => {
    if (instance.type !== type) return false;
    const scopedLanding = applyLandingSegmentInstance(landing, instance);
    return isLandingSegmentInstanceVisible(instance, scopedLanding, productsEnabled);
  });
}

export function applyLandingSegmentInstance(landing, segment) {
  if (!segment) return landing;
  const hasSnapshot = Boolean(segment.data && typeof segment.data === "object");
  if (!hasSnapshot) return landing;

  const next = deepMerge(landing, segment.data);
  if (segment.visualStyle && next?.theme && hasSnapshot) {
    next.theme.template = updateSegmentVisualStyle(
      next.theme.template,
      segment.type,
      segment.visualStyle
    );
  }

  const visibilityPath = VISIBILITY_PATHS[segment.type];
  if (visibilityPath && hasSnapshot) setAtPath(next, visibilityPath, true);
  if (hasSnapshot && ["spotlight", "featured", "brands", "categories"].includes(segment.type)) {
    setAtPath(next, "sections.showProducts", true);
  }
  return next;
}

export function normalizeVisualStyle(value, fallback = "style-1") {
  const key = String(value || "")
    .trim()
    .toLowerCase();
  return STYLE_ALIASES[key] || fallback;
}

function styleFromTemplate(template) {
  const value = String(template || "").trim().toLowerCase();
  if (value.includes("commercial") || value.includes("comercial") || value.includes("split")) return "style-2";
  if (value.includes("minimal") || value.includes("dark")) return "style-3";
  return "style-1";
}

export function resolveNavbarStyle(navigation = {}, theme = {}) {
  return normalizeVisualStyle(
    navigation?.style ||
      navigation?.variant ||
      theme?.navbarStyle ||
      styleFromTemplate(theme?.template),
    "style-1"
  );
}

export function resolveFooterStyle(footer = {}, theme = {}) {
  return normalizeVisualStyle(
    footer?.style ||
      footer?.variant ||
      theme?.footerStyle ||
      styleFromTemplate(theme?.template),
    "style-1"
  );
}

export function navbarOffsetSx(style, enabled = true) {
  if (!enabled) return { xs: 0, md: 0 };
  const normalized = normalizeVisualStyle(style);
  return {
    xs: 72,
    md: normalized === "style-2" ? 122 : normalized === "style-3" ? 82 : 86,
  };
}
